import { Plus, Trash2 } from 'lucide-react';
import { PortalDsButton, PortalDsField, PortalDsInput, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { ClientUser } from 'src/pages/client-management/types/client.types';

interface CampaignEmailRecipientsPickerProps {
  portalUsers: ClientUser[];
  selectedPortalContactIds: string[];
  manualRecipients: Array<{ name: string; email: string }>;
  onPortalContactsChange: (ids: string[]) => void;
  onManualRecipientsChange: (recipients: Array<{ name: string; email: string }>) => void;
  error?: string;
  manualError?: string;
  disabled?: boolean;
  /** When true, portal contact checkboxes are disabled (e.g. no client selected). */
  portalContactsDisabled?: boolean;
}

export function CampaignEmailRecipientsPicker({
  portalUsers,
  selectedPortalContactIds,
  manualRecipients,
  onPortalContactsChange,
  onManualRecipientsChange,
  error,
  manualError,
  disabled = false,
  portalContactsDisabled = false,
}: CampaignEmailRecipientsPickerProps) {
  const togglePortalContact = (userId: string) => {
    if (selectedPortalContactIds.includes(userId)) {
      onPortalContactsChange(selectedPortalContactIds.filter((id) => id !== userId));
      return;
    }
    onPortalContactsChange([...selectedPortalContactIds, userId]);
  };

  const updateManualRecipient = (
    index: number,
    field: 'name' | 'email',
    value: string
  ) => {
    const next = manualRecipients.map((recipient, recipientIndex) => (
      recipientIndex === index ? { ...recipient, [field]: value } : recipient
    ));
    onManualRecipientsChange(next);
  };

  const addManualRecipient = () => {
    onManualRecipientsChange([...manualRecipients, { name: '', email: '' }]);
  };

  const removeManualRecipient = (index: number) => {
    onManualRecipientsChange(manualRecipients.filter((_, recipientIndex) => recipientIndex !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className={portalDs.headingH3}>
          Portal contacts
        </h3>
        <p className={cn(portalDs.caption, 'mt-1')}>
          Select existing client portal users to receive this schedule.
        </p>
        <div className="mt-3 max-h-[200px] overflow-y-auto rounded-lg border border-[#EDEEF2]">
          {portalContactsDisabled ? (
            <p className={cn(portalDs.bodySm, 'p-4')}>
              Select a client to choose portal contacts, or add manual recipients below.
            </p>
          ) : portalUsers.length === 0 ? (
            <p className={cn(portalDs.bodySm, 'p-4')}>
              No active portal contacts for this client.
            </p>
          ) : (
            <ul className="divide-y divide-[#EDEEF2]">
              {portalUsers.map((user) => {
                const isSelected = selectedPortalContactIds.includes(user.id);
                return (
                  <li key={user.id}>
                    <label className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-[#F8F9FB]">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={disabled || portalContactsDisabled}
                        onChange={() => togglePortalContact(user.id)}
                        className="size-4 rounded border-[#D1D5DB]"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium text-[#0D0F1A]">
                          {user.email}
                        </span>
                        {user.pending ? (
                          <span className={portalDs.caption}>
                            Invite pending
                          </span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3 className={portalDs.headingH3}>
              Manual recipients
            </h3>
            <p className={cn(portalDs.caption, 'mt-1')}>
              Add email addresses that are not portal users.
            </p>
          </div>
          <PortalDsButton
            type="button"
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={addManualRecipient}
          >
            <Plus
              className="size-3.5"
              aria-hidden
            />
            Add recipient
          </PortalDsButton>
        </div>

        {manualRecipients.length === 0 ? (
          <p className={cn(portalDs.bodySm, 'rounded-lg border border-dashed border-[#EDEEF2] p-4')}>
            No manual recipients added.
          </p>
        ) : (
          <div className="space-y-3">
            {manualRecipients.map((recipient, index) => (
              <div
                key={`manual-recipient-${index}`}
                className="grid gap-3 rounded-lg border border-[#EDEEF2] p-4 sm:grid-cols-[1fr_1fr_auto]"
              >
                <PortalDsField label="Name">
                  <PortalDsInput
                    value={recipient.name}
                    disabled={disabled}
                    placeholder="Jane Doe"
                    onChange={(event) => updateManualRecipient(index, 'name', event.target.value)}
                  />
                </PortalDsField>
                <PortalDsField label="Email">
                  <PortalDsInput
                    type="email"
                    value={recipient.email}
                    disabled={disabled}
                    placeholder="jane@example.com"
                    onChange={(event) => updateManualRecipient(index, 'email', event.target.value)}
                  />
                </PortalDsField>
                <div className="flex items-end">
                  <PortalDsButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    aria-label={`Remove manual recipient ${index + 1}`}
                    onClick={() => removeManualRecipient(index)}
                  >
                    <Trash2
                      className="size-4"
                      aria-hidden
                    />
                  </PortalDsButton>
                </div>
              </div>
            ))}
          </div>
        )}
        {manualError ? (
          <p
            className="mt-2 text-xs text-[#DC2626]"
            role="alert"
          >
            {manualError}
          </p>
        ) : null}
      </div>

      {error ? (
        <p
          className="text-xs text-[#DC2626]"
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
